/* eslint-disable default-case */
import React, { useMemo, useState, useRef } from "react";

import {
  Dialog,
  DialogContent,
  Box,
  Card,
  IconButton,
  Typography,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  Link,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  Download,
  Close as CloseIcon,
  PictureAsPdf,
  Image as ImageIcon,
  InsertDriveFile,
} from "@mui/icons-material";
import "../css/GalleryDialog.css";
import Delete from "@mui/icons-material/Delete";

import YesOrNot from "../../../components/decision-mode/YesOrNot";


const COLORS = {
  bg: "#F9FAFB",
  primary: "#012442",
  accent: "#FF3100",
};

const AIRBNB_SHADOW =
  "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)";



const resolveFileTypeFromUrl = (fileUrl = "") => {
  const normalizedUrl = String(fileUrl || "").toLowerCase();

  if (normalizedUrl.includes(".pdf")) return "pdf";
  if (normalizedUrl.includes(".jpeg") || normalizedUrl.includes(".jpg")) return "jpeg";
  if (normalizedUrl.includes(".png")) return "png";

  return "";
};




const resolveFileType = (typeValue = "", fileUrl = "") => {
  const normalizedType = String(typeValue || "").toLowerCase().trim();

  if (!normalizedType) return resolveFileTypeFromUrl(fileUrl);
  if (normalizedType.includes("/")) return normalizedType.split("/").pop();

  return normalizedType.replace(/^\./, "");
};

const getTypeMeta = (type = "") => {
  const t = String(type || "").toLowerCase();
  if (t.includes("pdf")) {
    return { label: "PDF", icon: <PictureAsPdf fontSize="small" />, chipBg: "rgba(255,49,0,0.10)" };
  }
  if (t.includes("png") || t.includes("jpg") || t.includes("jpeg")) {
    return { label: "IMG", icon: <ImageIcon fontSize="small" />, chipBg: "rgba(1,36,66,0.08)" };
  }
  return { label: "FILE", icon: <InsertDriveFile fontSize="small" />, chipBg: "rgba(1,36,66,0.08)" };
};

export default function GalleryDialog({ data = [], onDelete, disabled = false }) {
  const [openYesOrNot, setOpenYesOrNot] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleCloseYesOrNot = () => {
    setOpenYesOrNot(false);
    setPendingDelete(null);
  };

  const handleModalResult = async (value) => {
    if (!value || !pendingDelete) return;
    await onDelete?.(pendingDelete.item, pendingDelete.index);
  };

  const galleryData = useMemo(() => {
    if (Array.isArray(data)) {
      return data
        .map((item, index) => {
          if (typeof item === "string") {
            const fileUrl = item.trim();
            if (!fileUrl) return null;

            return {
              name: `Documento_${index + 1}`,
              type: resolveFileTypeFromUrl(fileUrl),
              url: fileUrl,
            };
          }

          if (item && typeof item === "object") {
            const fileUrl = item.url || item.source || "";
            if (!fileUrl) return null;

            return {
              ...item,
              name: String(item.name || `Documento_${index + 1}`),
              type: resolveFileType(item.type, fileUrl),
              url: fileUrl,
            };
          }

          return null;
        })
        .filter((item) => item && item.url);
    }

    if (typeof data === "string" && data.trim()) {
      const fileUrl = data.trim();
      return [
        {
          createdAt: "Today",
          name: "Documento",
          type: resolveFileTypeFromUrl(fileUrl),
          url: fileUrl,
        },
      ];
    }

    return [];
  }, [data]);

  const [open, setOpen] = useState(false);
  const carouselRef = useRef(null);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleDialogClose = (event, reason) => {
    void event;
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  };

  const handleDelete = (item, index) => {
    if (!onDelete || disabled) return;
    setPendingDelete({ item, index });
    setOpenYesOrNot(true);
  };

  const previewItems = galleryData.slice(0, 5);
  const hasItems = galleryData.length > 0;
  const hasMoreItems = galleryData.length > 5;
  const openButtonLabel = hasMoreItems
    ? `Ver mais (${galleryData.length})`
    : `Visualizar (${galleryData.length})`;

  const scroll = (direction) => {
    if (!carouselRef.current) return;
    const scrollAmount = 424;
    carouselRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const handleDownload = async (item) => {
    if (!item?.url) {
      alert("URL do arquivo inválida.");
      return;
    }

    const fileName = item.name || `arquivo_${Date.now()}`;
    try {
      const resp = await fetch(item.url, { mode: "cors" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectURL = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectURL;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectURL);
    } catch (err) {
      const a = document.createElement("a");
      a.href = item.url;
      a.setAttribute("download", fileName);
      a.setAttribute("target", "_blank");
      document.body.appendChild(a);
      a.click();
      a.remove();
      // alert("Tentativa direta de download (se abrir em nova aba, salve por lá).");
    }
  };

  const debugItem = (item, index) => console.log(`[gallery] item[${index}]`, item);

  return (
    <div className="row gap-2 row justify-content-between align-items-center gx-3">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {previewItems.map((item, index) => {
          const itemName = String(item.name || `Documento_${index + 1}`);
          const itemType = String(item.type || resolveFileTypeFromUrl(item.url) || "file");
          const meta = getTypeMeta(itemType);

          return (
            <Link
              key={index}
              href={item.url}
              download={item.url}
              underline="none"
              target="_blank"
              className="gallery-pill"
              sx={{
                fontWeight: 400,
                color: COLORS.bg,
                backgroundColor: COLORS.primary,
                border: "1px solid rgba(255,49,0,0.32)",
                boxShadow: AIRBNB_SHADOW,
                "&:hover": {
                  backgroundColor: COLORS.primary,
                  color: COLORS.bg,
                },
              }}
            >
              <span className="gallery-pill__icon">{meta.icon}</span>

              <span className="gallery-pill__text" title={itemName}>
                {itemName}
              </span>

              <span className="gallery-pill__divider" />

              <Chip
                size="small"
                label={meta.label}
                className="gallery-pill__chip"
                sx={{
                  height: 22,
                  fontWeight: 400,
                  background: meta.chipBg,
                  color: COLORS.primary,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Link>
          );
        })}

        {hasItems && (
          <Box
            component="a"
            href="#"
            role="button"
            className="gallery-more-btn"
            onClick={(event) => {
              event.preventDefault();
              handleClickOpen();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleClickOpen();
              }
            }}
            sx={{
              alignItems: "center",
              backgroundColor: COLORS.primary,
              borderRadius: 999,
              boxShadow: AIRBNB_SHADOW,
              color: COLORS.bg,
              cursor: "pointer",
              display: "inline-flex",
              fontSize: 14,
              fontWeight: 400,
              minHeight: 36,
              px: 2,
              textDecoration: "none",
              "&:hover": {
                backgroundColor: COLORS.primary,
                color: COLORS.bg,
                textDecoration: "none",
              },
            }}
          >
            {openButtonLabel}
          </Box>
        )}
      </div>

      <Dialog
        open={open}
        onClose={handleDialogClose}
        disableEscapeKeyDown
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            backgroundColor: COLORS.bg,
            boxShadow: AIRBNB_SHADOW,
          },
        }}
      >
        {/* Header “Airbnb-ish”: branco, clean, com CTA discreto */}
        <AppBar
          position="relative"
          elevation={0}
          sx={{ background: COLORS.primary, color: COLORS.bg }}
        >
          <Toolbar sx={{ minHeight: 64, gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 400, fontSize: 18, lineHeight: "22px" }}>
                Documentação
              </Typography>
              <Typography sx={{ fontWeight: 400, fontSize: 12, opacity: 0.85 }}>
                {galleryData.length} item(ns)
              </Typography>
            </Box>

            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                ml: 0.5,
                borderRadius: 999,
                border: "1px solid rgba(249,250,251,0.42)",
                color: COLORS.bg,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <Divider sx={{ borderColor: "rgba(255,49,0,0.35)" }} />
        </AppBar>

        <DialogContent className="gallery-content" style={{ backgroundColor: COLORS.bg }}>
          <IconButton
            className="gallery-arrow gallery-arrow-left"
            onClick={() => scroll("left")}
            sx={{
              backgroundColor: COLORS.primary,
              color: COLORS.bg,
              boxShadow: AIRBNB_SHADOW,
              border: "1px solid rgba(255,49,0,0.35)",
              "&:hover": { backgroundColor: COLORS.primary },
            }}
          >
            <ArrowBackIosNew />
          </IconButton>

          <Box className="gallery-carousel-container">
            <Box ref={carouselRef} className="gallery-carousel-box">
              {galleryData.length > 0 ? (
                galleryData.map((item, index) => {
                  const fileUrl = item?.url || "";
                  const fileType =
                    (item?.type || "").toString().toLowerCase() ||
                    (fileUrl.includes(".pdf") ? "pdf" : "png");
                  const fileName = item?.name || `arquivo_${index + 1}`;

                  const meta = getTypeMeta(fileType);

                  return (
                    <Card
                      key={index}
                      className="gallery-card"
                      onDoubleClick={() => debugItem(item, index)}
                      sx={{
                        borderRadius: 4,
                        backgroundColor: "rgba(1,36,66,0.05)",
                        border: "1px solid rgba(1,36,66,0.26)",
                        boxShadow: AIRBNB_SHADOW,
                        overflow: "hidden",
                      }}
                    >
                      {/* Top bar com tag (tipo) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 1,
                          backgroundColor: "rgba(1,36,66,0.08)",
                          borderBottom: "1px solid rgba(1,36,66,0.16)",
                        }}
                      >
                        <Chip
                          size="small"
                          icon={meta.icon}
                          label={meta.label}
                          sx={{
                            height: 24,
                            fontWeight: 400,
                            background: meta.chipBg,
                            color: COLORS.primary,
                            "& .MuiChip-label": { px: 1 },
                            "& .MuiChip-icon": { color: COLORS.primary },
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: "rgba(1,36,66,0.70)",
                          }}
                        >
                          {index + 1}/{galleryData.length}
                        </Typography>
                      </Box>

                      <Box className="gallery-card-media-container">
                        {fileType.includes("pdf") ? (
                          <iframe
                            src={fileUrl}
                            title={fileName}
                            width="100%"
                            height="100%"
                            className="gallery-card-iframe"
                          />
                        ) : (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            className="gallery-card-img"
                            loading="lazy"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/600x400?text=Imagem+indispon%C3%ADvel";
                            }}
                          />
                        )}
                      </Box>

                      <CardContent className="gallery-card-content" sx={{ pb: 1 }}>
                        <Typography
                          component="div"
                          noWrap
                          sx={{
                            fontWeight: 400,
                            fontSize: 14,
                            color: COLORS.primary,
                          }}
                          title={fileName}
                        >
                          {fileName}
                        </Typography>
                      </CardContent>

                      <CardActions className="gallery-card-actions" sx={{ px: 1.5, pb: 1.5 }}>
                        <Tooltip title="Baixar" placement="top">
                          <IconButton
                            aria-label="download"
                            onClick={() => handleDownload(item)}
                            sx={{
                              borderRadius: 2,
                              border: "1px solid rgba(255,49,0,0.35)",
                              boxShadow: "none",
                              backgroundColor: COLORS.primary,
                              color: COLORS.bg,
                              "&:hover": { backgroundColor: COLORS.accent },
                            }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>

                        {onDelete && (
                          <Tooltip title="Excluir" placement="top">
                            <IconButton
                              color="error"
                              aria-label="delete"
                              onClick={() => handleDelete(item, index)}
                              disabled={disabled}
                              sx={{
                                borderRadius: 2,
                                border: "1px solid rgba(255,49,0,0.35)",
                                boxShadow: "none",
                                backgroundColor: COLORS.primary,
                                color: COLORS.bg,
                                "&:hover": { backgroundColor: COLORS.accent },
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* CTA secundário “Airbnb”: pequeno detalhe em vermelho */}
                        <Box sx={{ flex: 1 }} />
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: COLORS.accent,
                            opacity: 0.85,
                          }}
                        />
                      </CardActions>
                    </Card>
                  );
                })
              ) : (
                <Typography className="gallery-empty-text" sx={{ color: "rgba(1,36,66,0.65)" }}>
                  Nenhum item na galeria.
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton
            className="gallery-arrow gallery-arrow-right"
            onClick={() => scroll("right")}
            sx={{
              backgroundColor: COLORS.primary,
              color: COLORS.bg,
              boxShadow: AIRBNB_SHADOW,
              border: "1px solid rgba(255,49,0,0.35)",
              "&:hover": { backgroundColor: COLORS.primary },
            }}
          >
            <ArrowForwardIos />
          </IconButton>
        </DialogContent>
      </Dialog>


      <YesOrNot
        open={openYesOrNot}
        handleClose={handleCloseYesOrNot}
        onResult={handleModalResult}
      />
    </div>
  );
}
